import { Cloud, Layers, Mountain, Rows3, Waves, BrickWall, EyeOff, Ban, Grid2x2, TreePine, PanelTop, Square, type LucideIcon } from 'lucide-react'

import { API_BASE } from '@/api/client'
import type { SceneryBand, StageAnnotationState, StageRow } from '@/api/stages'

/**
 * The words the Backgrounds pages use for what the server calls features, flags, worlds and
 * review states — plain words for a parent, never ids. Unknown values are read as words
 * (`x:lava_pool` → "Lava pool"), so a newer backend never shows a raw key.
 */

/** `lava_pool` / `x:lava_pool` → "Lava pool". */
export function prettyWord(id: string | null | undefined): string {
  const t = (id ?? '').replace(/^x:/, '').replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
}

/** What a place offers, as a chip. */
export const FEATURE_INFO: Record<string, { label: string; icon: LucideIcon }> = {
  water: { label: 'Water', icon: Waves },
  ground: { label: 'Ground', icon: Mountain },
  sky: { label: 'Sky', icon: Cloud },
  wall: { label: 'Walls', icon: BrickWall },
  ceiling: { label: 'Ceiling', icon: PanelTop },
  floor: { label: 'Floor', icon: Grid2x2 },
  platform: { label: 'Ledges', icon: Rows3 },
  obstacle: { label: 'Things in the way', icon: Ban },
  walk_behind: { label: 'Walk behind', icon: Layers },
  hide_spot: { label: 'Hiding places', icon: EyeOff },
  scenery: { label: 'Scenery', icon: TreePine },
  foreground: { label: 'Foreground', icon: Square },
}

/** The order chips appear in (the ones a story asks for first). */
const FEATURE_ORDER = ['ground', 'water', 'sky', 'floor', 'wall', 'ceiling', 'platform', 'hide_spot', 'walk_behind', 'obstacle', 'scenery', 'foreground']

export function featureLabel(id: string): string {
  return FEATURE_INFO[id]?.label ?? prettyWord(id)
}

/** The features a row has, in chip order. */
export function presentFeatures(features: Record<string, boolean>): string[] {
  const on = Object.entries(features)
    .filter(([, v]) => v)
    .map(([k]) => k)
  return on.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

function rank(id: string): number {
  const i = FEATURE_ORDER.indexOf(id)
  return i < 0 ? FEATURE_ORDER.length : i
}

/** Flags the server raises, in words. */
const FLAG_WORDS: Record<string, string> = {
  no_ground: 'Nowhere to stand',
  no_water: 'No water marked',
  no_sky: 'No sky marked',
  water_on_roof: 'Water marked on a roof',
  water_indoors: 'Water marked indoors',
  bad_depth: 'Sizes look wrong',
  depth_suspicious: 'Sizes look wrong',
  horizon_low: 'Horizon too low',
  horizon_high: 'Horizon too high',
  not_published: 'Not used by stories yet',
  unpublished_changes: 'Changes not used yet',
  lint_errors: 'Something to fix',
  no_annotation: 'Places not marked yet',
  name_mismatch: 'Name and picture disagree',
  world_mismatch: 'Might be in the wrong world',
}

/** The server writes most flags as sentences already; an id-like one is put into words. */
export function flagLabel(flag: string): string {
  if (/\s/.test(flag.trim())) return flag.trim()
  return FLAG_WORDS[flag] ?? prettyWord(flag)
}

/** The id the server files a stage with no world under (a filter chip, never a world to pick). */
export const NO_WORLD = 'other'

/** `forest` → "Forest", `everyday` → "Everyday". */
export function worldLabel(world: string | null | undefined): string {
  if (world === NO_WORLD) return 'No world'
  return prettyWord(world) || 'No world'
}

export const BAND_LABEL: Record<SceneryBand, string> = { sky: 'In the sky', mid: 'In the middle', ground: 'On the ground' }
export const BAND_SHORT: Record<SceneryBand, string> = { sky: 'Sky', mid: 'Middle', ground: 'Ground' }

/** Ambient clips in words ("sway" → "Sways gently"). */
const CLIP_WORDS: Record<string, string> = {
  sway: 'Sways gently',
  drift: 'Drifts',
  bob: 'Bobs up and down',
  spin: 'Spins',
  flutter: 'Flutters',
  swim: 'Swims',
  float: 'Floats',
  idle: 'Breathes',
  shake: 'Shakes',
  wave: 'Waves',
  flicker: 'Flickers',
  twinkle: 'Twinkles',
  blink: 'Blinks',
  fly: 'Flies',
  walk: 'Walks',
}

export function clipLabel(clip: string | null | undefined): string {
  if (!clip) return 'Stands still'
  return CLIP_WORDS[clip] ?? prettyWord(clip)
}

/** The review state of a stage's places, in words, with a tone. */
export function placesState(a: StageAnnotationState): { label: string; tone: 'ok' | 'warn' | 'neutral' | 'danger'; hint: string } {
  if (a.lint_errors > 0) {
    return { label: a.lint_errors === 1 ? '1 thing to fix' : `${a.lint_errors} things to fix`, tone: 'danger', hint: 'Stories cannot use the latest changes until these are fixed.' }
  }
  if (a.published_rev === null) return { label: 'Not used by stories yet', tone: 'warn', hint: 'Check the places, then use them in stories.' }
  if (a.rev > a.published_rev) return { label: 'Newer changes not in use', tone: 'warn', hint: 'Stories still use the version published before your last changes.' }
  return { label: 'In use by stories', tone: 'ok', hint: 'Stories use these places.' }
}

/** True when a card should say "needs a look" (the server's filter may know more). */
export function needsLook(row: Pick<StageRow, 'flags' | 'annotation'>): boolean {
  return row.flags.length > 0 || row.annotation.lint_errors > 0
}

/** `/api/media/{uid}/{variant}` — the uid goes in verbatim. */
export function stageMedia(uid: string, variant: string): string {
  return `${API_BASE}/api/media/${uid}/${variant}`
}

/** The big picture to lay scenery over: the server's poster, else the plate's preview. */
export function stagePicture(uid: string, row?: Pick<StageRow, 'poster'> | null): string {
  if (row?.poster) return row.poster
  return uid.startsWith('live:') ? stageMedia(uid, 'poster.jpg') : stageMedia(uid, 'preview.webp')
}

export function stageThumb(uid: string, row?: Pick<StageRow, 'thumb'> | null): string {
  return row?.thumb ?? stageMedia(uid, 'thumb.webp?w=320')
}
